import {useEffect, useRef} from "react";

import {type FirestoreError, doc, onSnapshot} from "firebase/firestore";
import type {ZodType} from "zod/v4";

import {BaseCollectionConfig} from "~/api/BaseCollectionConfig";
import {firestore} from "~/firebase/firebase";

import {queryClient} from "../queryClient";

// This factory sets up the subscription and keeps data in sync
export const getByIdRealtimeSubFactory = <
  T extends ZodType,
  CollectionArgs extends NonNullable<unknown>,
>(
  config: BaseCollectionConfig<T, CollectionArgs>,
) => {
  return (key: CollectionArgs & {id?: string}) => {
    const retryTimeout = useRef<NodeJS.Timeout>(null);

    useEffect(() => {
      // Skip if any key segments are missing
      if (Object.values(key).some((segment) => !segment)) {
        return;
      }

      let unsubscribe: (() => void) | undefined;

      const subscribe = () => {
        const docRef = doc(firestore, ...config.getDocumentPath(key));

        unsubscribe = onSnapshot(
          docRef,
          (snapshot) => {
            try {
              const data = config.parseData(snapshot.id, snapshot.data());
              queryClient.setQueryData(config.getDocumentPath(key), data);
            } catch (err) {
              console.error("Error handling snapshot: ", err, snapshot.data());
            }
          },
          (error: FirestoreError) => {
            console.error("Error listening to document: ", error);

            // Retry subscription for any error
            if (retryTimeout.current) {
              clearTimeout(retryTimeout.current);
            }
            retryTimeout.current = setTimeout(() => {
              subscribe();
            }, 5000); // Retry after a 5-second delay
          },
        );
      };

      // Initiate the subscription
      subscribe();

      // Cleanup on unmount or if the key changes
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
        if (retryTimeout.current) {
          clearTimeout(retryTimeout.current);
        }
      };
    }, [JSON.stringify(key)]); // Use key in dependency array for re-subscribing on key change

    return null;
  };
};
